import { useMemo, useState } from 'react';

import type { IdAdMediumDto, IdPlacementDto } from '@/services/ws-point-of-sale/src/main/resources/rest-api';
import { getAllAdPlacements } from '@/services/ws-point-of-sale/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { NotActive, useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY, DROP_DOWN_ENTITY_LIMIT } from 'src/constants';
import type { Option } from 'src/types';

type UsePosPlacementOptionsParams = {
  adMediumId: IdAdMediumDto | undefined;
};

type UsePosPlacementOptionsReturn = {
  options: Option<IdPlacementDto>[];
  pending: boolean;
  total: number;
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
};

export const usePosPlacementOptions = ({ adMediumId }: UsePosPlacementOptionsParams): UsePosPlacementOptionsReturn => {
  const [searchFilter, setSearchFilter] = useState<string>('');

  const getPlacementListRequest = useRequest({
    apiMethod: getAllAdPlacements,
    params: adMediumId
      ? {
          placementFilterParams: {
            placementFilter: { adMediumId, nameFilter: searchFilter },
            paginationParams: { limit: DROP_DOWN_ENTITY_LIMIT, page: 0 },
          },
        }
      : NotActive,
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    getPlacementListRequest.data$,
    (response) =>
      response?.data.items.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? [],
  );
  const total = useSubscribe(getPlacementListRequest.data$, (result) => result?.data?.total ?? 0);
  const pending = useSubscribe(getPlacementListRequest.pending$);

  return useMemo(
    () => ({
      options,
      pending,
      total,
      searchFilter,
      setSearchFilter,
    }),
    [options, pending, total, searchFilter],
  );
};
